import { BaseInputFilter } from "domain/models/base-input-filter";
import {
  useCreateDocumentMutation,
  useDeleteDocumentMutation,
} from "presentation/hooks/use-query-document";
import { useJobTitleByFilter } from "presentation/hooks/use-query-job-title";
import { useParameterByFilter } from "presentation/hooks/use-query-parameter";

import {
  useCreatePurchaseOrderMutation,
  usePurchaseOrdersById,
  useUpdatePurchaseOrderMutation,
} from "presentation/hooks/use-query-purchase-order";
import { useParameterStore } from "presentation/stores/use-parameter-store";
import { usePurchaseOrderStore } from "presentation/stores/use-purchase-order-store";
import { Button } from "primereact/button";
import { TabPanel, TabView } from "primereact/tabview";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./index.css";
import PurchaseOrderInfoForm from "./PurchaseOrderInfoForm";
import PurchaseOrderStatusHistoryTab from "./PurchaseOrderStatusHistoryTab";
import AttachedDocumentsTab from "presentation/components/Shared/AttachedDocumentsTab";
import assetRepository from "../../../../infra/repositories/asset-repository";
import { useCreateAttachedDocumentMutation } from "presentation/hooks/use-query-attached-document";
import { usePendingDocumentsStore } from "presentation/stores/use-pending-documents-store";
import Spinner from "presentation/components/Shared/Spinner";

const PurchaseOrderForm: React.FC = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const { id } = useParams();
  const [activeIndex, setActiveIndex] = useState(0);
  const { purchaseOrder, setPurchaseOrder, resetPurchaseOrder } =
    usePurchaseOrderStore();
  const { setPurchaseOrderStatus, setPurchaseOrderType } = useParameterStore();
  
  const uploadMutation = useCreateAttachedDocumentMutation();
  const { pendingDocuments, clearPendingDocuments } = usePendingDocumentsStore();
  const {
    mutate: createPurchaseOrderMutation,
    error: creationError,
    isPending: isCreationPending,
    isError: isCreationError,
    isSuccess: isCreationSuccess,
    data: purchaseOrderCreated,
  } = useCreatePurchaseOrderMutation();
  const {
    mutate: updatePurchaseOrderMutation,
    error: updatingError,
    isPending: isUpdatePending,
    isError: isUpdatingError,
    isSuccess: isUpdatingSuccess,
  } = useUpdatePurchaseOrderMutation();
  const {
    mutate: createDocumentMutation,
    error: creationDocError,
    isError: isCreationDocError,
    isSuccess: isCreationDocSuccess,
    data: documentCreated,
  } = useCreateDocumentMutation();
  const {
    mutate: deleteDocumentMutation,
    error: deletingDocError,
    isError: isDeletingDocError,
    isSuccess: isDeletingDocSuccess,
  } = useDeleteDocumentMutation();
  const { data, isError, isLoading, error } = usePurchaseOrdersById(id);

  const { data: parameters } = useParameterByFilter({
    limit: 1000,
    sortBy: "value",
  } as BaseInputFilter);
  const { data: jobTitles } = useJobTitleByFilter({
    limit: 100,
  } as BaseInputFilter);

  useEffect(() => {
    setPurchaseOrderStatus(
      parameters?.data?.filter((item) => item.type === "purchaseOrderStatus")
    );

    setPurchaseOrderType(
      parameters?.data?.filter((item) => item.type === "purchaseOrderType")
    );
  }, [parameters]);

  useEffect(() => {
    if (data && data.data) {
      setPurchaseOrder(data.data);
    } else if (isError) {
      console.error("Erro ao carregar dados do colaborador:", error);
    }
  }, [data, isError, error]);

  const handleNextTab = async () => {
    if (purchaseOrder) {
      !purchaseOrder.id
        ? createPurchaseOrderMutation(purchaseOrder)
        : updatePurchaseOrderMutation(purchaseOrder);
    }
  };

  const handleCancel = () => {
    resetPurchaseOrder();
    navigate("/purchaseOrders");
  };

  useEffect(() => {
    if (isCreationError || isUpdatingError) {
      const err = isCreationError ? creationError : updatingError;
      console.log("Error caught:", err);
      
      let message: string[] = [];
      if (err && (err as any).errors && Array.isArray((err as any).errors)) {
        message = (err as any).errors;
      } else if (err && err.message) {
        message = [err.message];
      } else {
        message = ["Ocorreu um erro inesperado"];
      }

      console.log("message to show:", message);
      message.forEach((item: string) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: item || "Ocorreu um erro inesperado",
          life: 1500,
        });
      });
    }
  }, [creationError, updatingError]);

  useEffect(() => {
    if (isCreationDocError || isDeletingDocError) {
      let message = isCreationError
        ? creationDocError.message
        : deletingDocError.message;

      if (
        creationDocError.response.data.metadata.errors &&
        creationDocError.response.data.metadata.errors.length > 0
      ) {
        message = creationDocError.response.data.metadata.errors;
      } else if (
        deletingDocError.response.data.metadata.errors &&
        deletingDocError.response.data.metadata.errors.length > 0
      ) {
        message = deletingDocError.response.data.metadata.errors;
      }

      message.map((item: string) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: item || "Ocorreu um erro inesperado",
          life: 1500,
        });
      });
    }
  }, [creationDocError, deletingDocError]);

  useEffect(() => {
    if (isCreationSuccess || isUpdatingSuccess) {
      const purchaseOrderId =
        purchaseOrder?.id || purchaseOrderCreated?.data?.id;

      if (!purchaseOrderId) {
        console.error("Erro: purchaseOrderId está undefined!");
        return;
      }

      const executeSave = async () => {
        // Handle pending documents
        const docsToUpload = pendingDocuments.filter(d => d.objectType === "PURCHASE_ORDER");
        if (docsToUpload.length > 0) {
          try {
            for (const doc of docsToUpload) {
              const folder = `attached-documents/purchase_order/${purchaseOrderId}`;
              const uploadRes = await assetRepository.uploadAsset(folder, doc.file);
              const payload = {
                objectType: "PURCHASE_ORDER",
                objectId: purchaseOrderId,
                name: doc.name,
                fileKey: uploadRes.filePath,
              };
              await uploadMutation.mutateAsync(payload);
            }
            clearPendingDocuments("PURCHASE_ORDER");
          } catch (error) {
            console.error("Error uploading pending documents:", error);
            toast.current?.show({
              severity: "error",
              summary: "Erro nos Documentos",
              detail: "A PO foi gravada, mas ocorreu um erro ao carregar alguns documentos anexos.",
              life: 5000,
            });
          }
        }

        const successMessage = isCreationSuccess
          ? "Ordem de Compra registrada com sucesso"
          : "Ordem de Compra atualizada com sucesso";

        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: successMessage,
          life: 1500,
        });

        setTimeout(() => {
          resetPurchaseOrder();
          navigate("/purchaseOrders");
        }, 2000);
      };
      
      executeSave();
    }
  }, [
    isCreationSuccess,
    isUpdatingSuccess,
    resetPurchaseOrder,
    navigate,
    purchaseOrderCreated,
  ]);

  if (isUpdatePending || isCreationPending) return <Spinner/>;

  return (
    <div className="card" style={{ paddingBottom: "100px" }}>
      <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
        <TabPanel header="Ordem de Compra" leftIcon="pi pi-wallet mr-2">
          <PurchaseOrderInfoForm />
        </TabPanel>
        <TabPanel header="Documentos" leftIcon="pi pi-file mr-2">
          <AttachedDocumentsTab
            objectType="PURCHASE_ORDER"
            objectId={purchaseOrder?.id}
          />
        </TabPanel>
        {purchaseOrder?.id && (
          <TabPanel header="Histórico de Status" leftIcon="pi pi-history mr-2">
            <PurchaseOrderStatusHistoryTab />
          </TabPanel>
        )}
      </TabView>
      <div className="next-button default-4-cols" style={{ display: "flex", gap: "10px", justifyContent: "flex-end", position: "fixed", bottom: 0, right: 0, left: "15vw", width: "auto", backgroundColor: "var(--surface-card)", padding: "1rem 2rem", zIndex: 1000, borderTop: "1px solid var(--surface-border)", borderLeft: "1px solid var(--surface-border)", borderTopLeftRadius: "8px", boxShadow: "0 -2px 10px rgba(0,0,0,0.1)" }}>
        <Button
          label="Cancelar"
          onClick={handleCancel}
          className="button-size"
          outlined
          style={{
            backgroundColor: "white",
            borderColor: "#2196f3",
            color: "#2196f3",
            borderRadius: "8px",
          }}
          type="button"
        />
        <Button
          label="Guardar"
          onClick={handleNextTab}
          className="button-size"
        />
      </div>
      <Toast ref={toast} position="bottom-right" />
    </div>
  );
};
export default PurchaseOrderForm;
